import React, { Component } from "react";
import moment from "moment";
import { OFFERLIST, CAN_USE_VENDOR_OFFERS, hasPermission } from "../../utils/permissons";

const IdAndBadgeHeader = ({ purchaseOrderId, status }) => {
  return (
    <div className="d-flex flex-row align-items-center">
      <h4 className="pr-1 font-weight-medium no-margin-bottom">Purchase Order #{purchaseOrderId}</h4>
      <div className={`badge badge-pill custom-badge ${status.badgeClass} hidden-xs-down`}>{status.name}</div>
    </div>
  );
};

const Button = ({ onClick, text, small, danger, icon }) => {
  return (
    <button
      type="button"
      className={`${small ? "hidden-sm-up btn-sm" : "hidden-xs-down"} btn ${danger ? "btn-danger" : "btn-primary"} ml-05 mr-0`}
      onClick={onClick}
    >
      {text}
      {icon && <i className={`fa fa-${icon}`}></i>}
    </button>
  );
};

const InProgressHeader = ({
  purchaseOrderId,
  purchaseOrderVendor,
  status,
  purchaseOrderDate,
  handlePurchaseOrderSubmit,
  handleDelete,
  deliveryDate,
  deliveryDateError,
  handleDeliveryDate,
  isFromMarket,
}) => {
  return (
    <div className="card">
      <div className="row">
        <div className="col-sm-12">
          <div className="card-header-grey d-flex justify-content-between align-items-center">
            <IdAndBadgeHeader purchaseOrderId={purchaseOrderId} status={status} />
            <span className="d-flex">
              <Button onClick={handlePurchaseOrderSubmit} text={isFromMarket ? "Finish purchase" : "Wait purchase"} />
              <Button onClick={handlePurchaseOrderSubmit} small icon="paper-plane" />
              <Button onClick={handleDelete} danger icon="trash" />
              <Button onClick={handleDelete} danger small icon="trash" />
            </span>
          </div>
        </div>
      </div>
      <div className="card-block">
        <div className={`${deliveryDateError ? "has-danger order-delivery-date" : "order-delivery-date"}`}>
          <label htmlFor="delivery-date" className="col-form-label">
            Delivery Date
          </label>
          <input
            className="form-control col-sm-4"
            type="date"
            id="delivery-date"
            value={deliveryDate}
            onChange={(date) => handleDeliveryDate(date)}
          />
          {deliveryDateError && <small className="error form-control-feedback">The delivery date cannot be in the past</small>}
        </div>
        <div className="row pt-2">
          <div className="col-sm-3">
            <h5>Vendor</h5>
            <p>{purchaseOrderVendor}</p>
          </div>
          <div className="col-sm-3">
            <h5>Date</h5>
            <p>{purchaseOrderDate}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const AwaitingPurchaseHeaderNoInvoice = ({
  purchaseOrderId,
  status,
  handleAssociateInvoiceSubmit,
  handleNewInvoiceSubmit,
  handlePrint,
  handleDelete,
  purchaseOrderDeliveryDate,
}) => {
  return (
    <div className="card">
      <div className="row">
        <div className="col-sm-12">
          <div className="card-header-grey d-flex justify-content-between align-items-center flex-column-768">
            <IdAndBadgeHeader purchaseOrderId={purchaseOrderId} status={status} />
            <div className="d-flex">
              <Button onClick={handleAssociateInvoiceSubmit} text="Associate invoice" />
              <Button onClick={handleAssociateInvoiceSubmit} small icon="link" />
              <div className="pl-h">
                <Button onClick={handleNewInvoiceSubmit} text="New invoice" />
                <Button onClick={handleNewInvoiceSubmit} small icon="plus" />
              </div>
              <div className="pl-h">
                <Button onClick={handlePrint} text="Print" />
                <Button onClick={handlePrint} small icon="print" />
              </div>
              <div className="pl-h">
                <Button onClick={handleDelete} danger icon="trash" />
                <Button onClick={handleDelete} danger small icon="trash" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="card-block">
        <div className="row">
          <div className="col-sm-3">
            <h5>Estimated Delivery Date</h5>
            <p>{purchaseOrderDeliveryDate ? moment(purchaseOrderDeliveryDate).format("L") : "-"}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const EditInvoiceNumberOrAmount = ({
  field,
  fieldValue,
  fieldError,
  isFieldUpdating,
  handleFieldEdit,
  handleFieldEditChange,
  handleFieldEditSubmit,
  handleFieldEditCancel,
}) => {
  return isFieldUpdating ? (
    <div className="d-flex col-12">
      <div className={fieldError ? "row has-danger" : "row"}>
        <input
          type={`${field === "amount" ? "number" : "text"}`}
          id="field-input"
          name="field-input"
          className={fieldError ? "form-control form-control-danger" : "form-control"}
          placeholder={`Enter invoice ${field}`}
          value={fieldValue}
          onChange={(value) => handleFieldEditChange(value)}
        />
        {fieldError ? (
          <em id="firstname-error" className="error form-control-feedback font-size-11">
            {field === "amount" ? "Please enter a positive number." : "Field required."}
          </em>
        ) : (
          <em></em>
        )}
      </div>
      <div className="d-flex">
        <button className="ml-1 mr-05 btn-sm btn btn-primary" onClick={handleFieldEditSubmit}>
          <i className="fa fa-check"></i>
        </button>
        <button className="mr-0 btn-sm btn btn-danger" onClick={handleFieldEditCancel}>
          <i className="fa fa-times"></i>
        </button>
      </div>
    </div>
  ) : (
    <div className="d-flex align-items-center">
      <p>{fieldValue}</p>
      <p className="d-flex">
        <i className="ml-05 mr-0 cursor-pointer fa fa-edit" onClick={handleFieldEdit} />
      </p>
    </div>
  );
};

const AwaitingPurchaseHeaderWithInvoice = ({
  purchaseOrderInvoice,
  purchaseOrderVendor,
  purchaseOrderInvoiceAmount,
  handleFinishPurchaseOrder,
  handlePrint,
  transportCondition,
  handleTransportCondition,
  purchaseOrderTransportConditions,
  transportTemperature,
  handleTransportTemperature,
  errorTemperature,
  submitting,
  newInvoice,
  invoiceError,
  isInvoiceUpdating,
  handleInvoiceEdit,
  handleInvoiceEditChange,
  handleInvoiceEditSubmit,
  handleInvoiceEditCancel,
  newAmount,
  amountError,
  isAmountUpdating,
  handleAmountEdit,
  handleAmountEditChange,
  handleAmountEditSubmit,
  handleAmountEditCancel,
}) => {
  const invoiceNumber = newInvoice ? newInvoice : isInvoiceUpdating ? "" : purchaseOrderInvoice;
  const invoiceAmount = newAmount ? newAmount : isAmountUpdating ? "" : purchaseOrderInvoiceAmount;
  return (
    <div className="card">
      <div className="row">
        <div className="col-sm-12">
          <div className="card-header-grey d-flex justify-content-between align-items-center">
            <div className="d-flex flex-row align-items-center">
              <h4 className="pr-1 font-weight-medium no-margin-bottom">Invoice</h4>
            </div>
            <div>
              <button
                type="button"
                className="btn btn-primary m-0 hidden-xs-down"
                onClick={handleFinishPurchaseOrder}
                disabled={submitting}
              >
                <i className="fa fa-check mr-05" />
                {submitting ? "Finishing..." : "Finish"}
              </button>
              <button type="button" className="btn btn-primary m-0 hidden-sm-up" onClick={handleFinishPurchaseOrder} disabled={submitting}>
                <i className="fa fa-check" />
              </button>
              <button type="button" className="btn btn-primary m-0 ml-05 hidden-xs-down" onClick={handlePrint}>
                <i className="fa fa-print mr-05" />
                Print
              </button>
              <button type="button" className="btn btn-primary m-0 ml-05 hidden-sm-up" onClick={handlePrint}>
                <i className="fa fa-print" />
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="card-block">
        <div className="row">
          <div className="col-sm-10 col-xl-6">
            <div className="form-group row">
              <label className="col-sm-4 col-form-label" htmlFor="select">
                Transport Condition
              </label>
              <div className="col-sm-8">
                <select id="select" name="select" className={"form-control"} value={transportCondition} onChange={handleTransportCondition}>
                  {purchaseOrderTransportConditions.map((condition) => {
                    return (
                      <option value={condition.id} key={condition.id}>
                        {condition.name}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>
            <div className={errorTemperature ? "form-group row has-danger" : "form-group row"}>
              <label className="col-sm-4 col-12 col-form-label" htmlFor="temperature-input">
                Transport Temperature
              </label>
              <div className="col-sm-7 col-10">
                <input
                  type="number"
                  id="temperature-input"
                  name="temperature-input"
                  className={errorTemperature ? "form-control form-control-danger" : "form-control"}
                  placeholder="Enter transport temperature"
                  value={transportTemperature}
                  onChange={handleTransportTemperature}
                />
              </div>
              <div className="col-sm-1 col-1 pl-0">
                <h4>
                  <span className="badge badge-pill badge-info font-sm">°F</span>
                </h4>
              </div>
              {errorTemperature ? (
                <em id="firstname-error" className="error form-control-feedback">
                  Please enter value.
                </em>
              ) : (
                <em></em>
              )}
            </div>
          </div>
          <div className="col-sm-12">
            <div className="transport-condition" />
          </div>
          <div className="col-sm-3">
            <h5>ID</h5>
            <EditInvoiceNumberOrAmount
              field="number"
              fieldValue={invoiceNumber}
              isFieldUpdating={isInvoiceUpdating}
              fieldError={invoiceError}
              handleFieldEdit={handleInvoiceEdit}
              handleFieldEditChange={handleInvoiceEditChange}
              handleFieldEditSubmit={handleInvoiceEditSubmit}
              handleFieldEditCancel={handleInvoiceEditCancel}
            />
          </div>
          <div className="col-sm-3">
            <h5>Vendor</h5>
            <p>{purchaseOrderVendor}</p>
          </div>
          <div className="col-sm-3">
            <h5>Amount</h5>
            <EditInvoiceNumberOrAmount
              field="amount"
              fieldValue={invoiceAmount}
              isFieldUpdating={isAmountUpdating}
              fieldError={amountError}
              handleFieldEdit={handleAmountEdit}
              handleFieldEditChange={handleAmountEditChange}
              handleFieldEditSubmit={handleAmountEditSubmit}
              handleFieldEditCancel={handleAmountEditCancel}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const AwaitingPurchaseHeaderAssociateInvoice = ({
  handleAssociateInvoice,
  handleInvoiceInput,
  error,
  handleFinishPurchaseOrder,
  handlePrint,
  transportCondition,
  handleTransportCondition,
  purchaseOrderTransportConditions,
  transportTemperature,
  handleTransportTemperature,
  errorTemperature,
  submitting,
}) => {
  return (
    <div className="card">
      <div className="row">
        <div className="col-sm-12">
          <div className="card-header-grey d-flex justify-content-between align-items-center">
            <div className="d-flex flex-row align-items-center">
              <h4 className="pr-1 font-weight-medium no-margin-bottom">Invoice</h4>
            </div>
            <div>
              <button
                type="button"
                className="btn btn-primary m-0 hidden-xs-down"
                onClick={handleFinishPurchaseOrder}
                disabled={submitting}
              >
                <i className="fa fa-check mr-05" />
                {submitting ? "Finishing..." : "Finish"}
              </button>
              <button type="button" className="btn btn-primary m-0 hidden-sm-up" onClick={handleFinishPurchaseOrder} disabled={submitting}>
                <i className="fa fa-check mr-05" />
              </button>
              <button type="button" className="btn btn-primary m-0 ml-05 hidden-xs-down" onClick={handlePrint}>
                <i className="fa fa-print mr-05" />
                Print
              </button>
              <button type="button" className="btn btn-primary m-0 ml-05 hidden-sm-up" onClick={handlePrint}>
                <i className="fa fa-print" />
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="card-block">
        <div className="row">
          <div className="col-sm-10 col-xl-6">
            <div className="form-group row">
              <label className="col-sm-4 col-form-label" htmlFor="select">
                Transport Condition
              </label>
              <div className="col-sm-8">
                <select id="select" name="select" className={"form-control"} value={transportCondition} onChange={handleTransportCondition}>
                  {purchaseOrderTransportConditions.map((condition) => {
                    return (
                      <option value={condition.id} key={condition.id}>
                        {condition.name}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>
            <div className={errorTemperature ? "form-group row has-danger" : "form-group row"}>
              <label className="col-sm-4 col-12 col-form-label" htmlFor="temperature-input">
                Transport Temperature
              </label>
              <div className="col-sm-7 col-10">
                <input
                  type="number"
                  id="temperature-input"
                  name="temperature-input"
                  className={errorTemperature ? "form-control form-control-danger" : "form-control"}
                  placeholder="Enter transport temperature"
                  value={transportTemperature}
                  onChange={handleTransportTemperature}
                />
              </div>
              <div className="col-sm-1 col-1 pl-0">
                <h4>
                  <span className="badge badge-pill badge-info font-sm">°F</span>
                </h4>
              </div>
              {errorTemperature ? (
                <em id="firstname-error" className="error form-control-feedback">
                  Please enter value.
                </em>
              ) : (
                <em></em>
              )}
            </div>
            <div className={error ? "form-group row has-danger" : "form-group row"}>
              <label className="col-sm-4 col-form-label" htmlFor="invoice">
                Invoice Number
              </label>
              <div className="col-sm-8 input-group">
                <div className="input-group-addon">#</div>
                <input
                  type="text"
                  id="invoice"
                  name="invoice"
                  className={error ? "form-control form-control-danger" : "form-control"}
                  placeholder="Enter invoice number"
                  onChange={handleInvoiceInput}
                />
              </div>
            </div>
          </div>
          <div className="col-sm-12">
            <div className="d-flex justify-content-end align-items-center">
              <button type="button" className="btn btn-primary m-0" onClick={handleAssociateInvoice}>
                Associate invoice
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const FinishedHeader = ({
  purchaseOrderId,
  status,
  handleSeeLabels,
  handleDuplicate,
  handleDuplicateByPrice,
  handlePrint,
  transportCondition,
  isFromMarket,
}) => {
  return (
    <div className="card">
      <div className="row">
        <div className="col-sm-12">
          <div className="card-header-grey d-flex justify-content-between align-items-center flex-column-768">
            <IdAndBadgeHeader purchaseOrderId={purchaseOrderId} status={status} />
            <div className="row">
              <div>
                {hasPermission(OFFERLIST, CAN_USE_VENDOR_OFFERS) && (
                  <button type="button" className="btn btn-primary m-0 mr-05" onClick={handleDuplicateByPrice}>
                    <i className="fa fa-list-alt  mr-05" />
                    <span className="hidden-xs-down">Duplicate by best price</span>
                  </button>
                )}
                <button type="button" className="btn btn-primary m-0 mr-05" onClick={handleDuplicate}>
                  <i className="fa fa-copy mr-05" />
                  <span className="hidden-xs-down">Duplicate</span>
                </button>
              </div>
              {!isFromMarket && (
                <div>
                  <button type="button" className="btn btn-primary m-0 hidden-xs-down" onClick={handleSeeLabels}>
                    <i className="fa fa-check mr-05" /> See Labels
                  </button>
                  <button type="button" className="btn btn-primary m-0 hidden-sm-up" onClick={handleSeeLabels}>
                    Labels
                  </button>
                  <button type="button" className="btn btn-primary m-0 ml-05" onClick={handlePrint}>
                    <i className="fa fa-print mr-05 hidden-xs-down" /> Print
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {!isFromMarket && (
        <div className="card-block">
          <div className="row">
            <div className="col-sm-4">
              <h5>Transport Condition</h5>
              <p>{transportCondition}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export class PurchaseOrderDetailHeader extends Component {
  render() {
    const {
      purchaseOrderId,
      purchaseOrderVendor,
      status,
      purchaseOrderDate,
      purchaseOrderDeliveryDate,
      handlePurchaseOrderSubmit,
      handleAssociateInvoiceSubmit,
      handleNewInvoiceSubmit,
      handlePrint,
      handleSeeLabels,
      handleDuplicate,
      handleDuplicateByPrice,
      hasInvoice,
      isAssociateInvoice,
      purchaseOrderInvoice,
      purchaseOrderInvoiceAmount,
      handleFinishPurchaseOrder,
      handleAssociateInvoice,
      handleInvoiceInput,
      invoice,
      error,
      transportCondition,
      handleTransportCondition,
      purchaseOrderTransportConditions,
      transportTemperature,
      handleTransportTemperature,
      errorTemperature,
      submitting,
      handleDelete,
      deliveryDate,
      deliveryDateError,
      handleDeliveryDate,
      newInvoice,
      invoiceError,
      isInvoiceUpdating,
      handleInvoiceEdit,
      handleInvoiceEditChange,
      handleInvoiceEditSubmit,
      handleInvoiceEditCancel,
      newAmount,
      amountError,
      isAmountUpdating,
      handleAmountEdit,
      handleAmountEditChange,
      handleAmountEditSubmit,
      handleAmountEditCancel,
      isFromMarket,
    } = this.props;
    if (status.id === 0) {
      return (
        <InProgressHeader
          purchaseOrderId={purchaseOrderId}
          purchaseOrderVendor={purchaseOrderVendor}
          status={status}
          purchaseOrderDate={purchaseOrderDate}
          handlePurchaseOrderSubmit={() => handlePurchaseOrderSubmit(purchaseOrderId)}
          handleDelete={handleDelete}
          deliveryDate={deliveryDate}
          deliveryDateError={deliveryDateError}
          handleDeliveryDate={handleDeliveryDate}
          isFromMarket={isFromMarket}
        />
      );
    }
    if (status.id === 1) {
      if (hasInvoice) {
        return (
          <AwaitingPurchaseHeaderWithInvoice
            purchaseOrderInvoice={purchaseOrderInvoice}
            purchaseOrderVendor={purchaseOrderVendor}
            purchaseOrderInvoiceAmount={purchaseOrderInvoiceAmount}
            handleFinishPurchaseOrder={handleFinishPurchaseOrder}
            handlePrint={handlePrint}
            transportCondition={transportCondition}
            handleTransportCondition={handleTransportCondition}
            purchaseOrderTransportConditions={purchaseOrderTransportConditions}
            transportTemperature={transportTemperature}
            handleTransportTemperature={handleTransportTemperature}
            errorTemperature={errorTemperature}
            submitting={submitting}
            newInvoice={newInvoice}
            invoiceError={invoiceError}
            isInvoiceUpdating={isInvoiceUpdating}
            handleInvoiceEdit={handleInvoiceEdit}
            handleInvoiceEditChange={handleInvoiceEditChange}
            handleInvoiceEditSubmit={handleInvoiceEditSubmit}
            handleInvoiceEditCancel={handleInvoiceEditCancel}
            newAmount={newAmount}
            amountError={amountError}
            isAmountUpdating={isAmountUpdating}
            handleAmountEdit={handleAmountEdit}
            handleAmountEditChange={handleAmountEditChange}
            handleAmountEditSubmit={handleAmountEditSubmit}
            handleAmountEditCancel={handleAmountEditCancel}
          />
        );
      } else if (isAssociateInvoice) {
        return (
          <AwaitingPurchaseHeaderAssociateInvoice
            handleAssociateInvoice={handleAssociateInvoice}
            handleFinishPurchaseOrder={handleFinishPurchaseOrder}
            handleInvoiceInput={handleInvoiceInput}
            handlePrint={handlePrint}
            invoice={invoice}
            error={error}
            transportCondition={transportCondition}
            handleTransportCondition={handleTransportCondition}
            purchaseOrderTransportConditions={purchaseOrderTransportConditions}
            transportTemperature={transportTemperature}
            handleTransportTemperature={handleTransportTemperature}
            errorTemperature={errorTemperature}
            submitting={submitting}
          />
        );
      } else {
        return (
          <AwaitingPurchaseHeaderNoInvoice
            purchaseOrderId={purchaseOrderId}
            status={status}
            handleAssociateInvoiceSubmit={handleAssociateInvoiceSubmit}
            handleNewInvoiceSubmit={handleNewInvoiceSubmit}
            handlePrint={handlePrint}
            handleDelete={handleDelete}
            purchaseOrderDeliveryDate={purchaseOrderDeliveryDate}
          />
        );
      }
    }
    if (status.id === 2) {
      return (
        <FinishedHeader
          purchaseOrderId={purchaseOrderId}
          status={status}
          handleSeeLabels={handleSeeLabels}
          handleDuplicate={handleDuplicate}
          handleDuplicateByPrice={handleDuplicateByPrice}
          handlePrint={handlePrint}
          transportCondition={transportCondition}
          isFromMarket={isFromMarket}
        />
      );
    }
  }
}

export default PurchaseOrderDetailHeader;
